import { ErrorHandler, Injectable } from '@angular/core';
import {HttpClient, HttpEvent} from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';

import { AuthService } from '../../auth/auth/auth.service';
import { UserInfo } from '../models/user-info.model';
import { environment } from '../../../environments/environment';
import { AuthEventType, IAuthEvent } from '../../auth/models/auth-event.model';
import { FavoriteAirport } from '../models/user-data/favorite-airport.model';
import { Review } from '../models/review.model';
import { ReviewSummary } from '../models/review-summary.model';
import { IEntity } from '../models/entity.model';
import { UserEcho } from '../models/user-echo.model';
import { Photo } from '../models/photo.model';
import { FavoriteEvent } from '../models/user-data/favorite-event.model';


export interface IUserService {
  /**
   * User info stream
   */
  readonly userInfo: BehaviorSubject<UserInfo>;

  /**
   * Gets the authenticated user's information. Note this method requires that the user is authenticated.
   * @returns {Promise<UserInfo>} User info
   */
  getUserInfo(): Promise<UserInfo>;

  /**
   * Adds the given airport identifier to the user's list of favorite airports. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {string} identifier Airport identifier to add.
   * @returns {Promise<FavoriteAirport>} Favorite airport record.
   */
  addFavoriteAirport(identifier: string): Promise<FavoriteAirport>;

  /**
   * Removes the given airport identifier from the user's list of favorite airports. Requires that the user is
   * authenticated, and that userInfo is loaded.
   * @param {string} identifier Airport identifier to remove.
   * @returns {Promise<void>}
   */
  removeFavoriteAirport(identifier: string): Promise<void>;

  /**
   * Gets a user review for an entity (airport or business). Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {IEntity} entity object containing summary information about this entity (airport or business).
   * @returns {Promise<Review>} User's review for entity from API.
   */
  getUserReview(entity: IEntity): Promise<Review>;

  /**
   * Gets the review summary for an entity (airport or business).
   * @param {IEntity} entity object containing summary information about this entity (airport or business).
   * @returns {Promise<ReviewSummary>} review summary for entity from API.
   */
  getReviewSummary(entity: IEntity): Promise<ReviewSummary>;

  /**
   * Sets the given airport identifier to the user's home airport. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {string} identifier Airport identifier to set.
   * @returns {Promise<string>} HOme airport record.
   */
  setHomeAirport(identifier: string): Promise<string>;

  /**
   * Removes the given airport identifier as the user's home airport. Requires that the user is
   * authenticated, and that userInfo is loaded.
   * @param {string} identifier Airport identifier to remove.
   * @returns {Promise<void>}
   */
  removeHomeAirport(identifier: string): Promise<void>;

  /**
   * Gets the authenticated user's UserEcho token. Note this method requires that the user is authenticated.
   * @returns {Promise<string>} SSO token
   */
  getUserechoInfo(): Promise<string>;
}


@Injectable()
export class UserService implements IUserService {
  /**
   * User info stream
   */
  userInfo: BehaviorSubject<UserInfo> = new BehaviorSubject<UserInfo>(null);

  constructor(private _http: HttpClient,
              private _auth: AuthService,
              private _errorHandler: ErrorHandler) {
    this._auth.authStream.subscribe(e => this._onAuthEvent(e));
  }

  private async _onAuthEvent(e: IAuthEvent): Promise<void> {
    if (!e) {
      return;
    }
    switch (e.type) {
      case AuthEventType.SignIn:
        await this.getUserInfo();
        break;
      case AuthEventType.InvalidSignIn:
      case AuthEventType.SignOut:
        this._clearUserInfo();
        break;
    }
  }

  private _clearUserInfo() {
    const userInfo = this.userInfo.getValue();
    if (userInfo !== null) {
      this.userInfo.next(null);
    }
  }

  /**
   * Gets the authenticated user's information. Note this method requires that the user is authenticated.
   * @returns {Promise<UserInfo>} User info
   */
  async getUserInfo(): Promise<UserInfo> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      return null;
    }
    try {
      const response = await this._http.get(`${environment.airportsApiUrl}/customer`).toPromise();
      const userInfo = UserInfo.parse(response);
      this.userInfo.next(userInfo);
      return userInfo;
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }


  /**
   * Adds the given airport identifier to the user's list of favorite airports. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {string} identifier Airport identifier to add.
   * @returns {Promise<FavoriteAirport>} Favorite airport record.
   */
  async addFavoriteAirport(identifier: string): Promise<FavoriteAirport> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
    }
    if (!identifier) {
      throw new Error('Missing identifier');
    }

    // Check if favorite already exists
    const existingFavorite = userInfo.favoriteAirports.find(f => f.identifier === identifier);
    if (existingFavorite) {
      return existingFavorite;
    }

    // Add the favorite
    const response = await this._http.post(`${environment.airportsApiUrl}/customer/favorites/${identifier}`, null).toPromise();
    const favoriteAirport = FavoriteAirport.parse(response);

    // Update userInfo
    userInfo.favoriteAirports.push(favoriteAirport);
    this.userInfo.next(userInfo);

    return favoriteAirport;
  }

  /**
   * Removes the given airport identifier from the user's list of favorite airports. Requires that the user is
   * authenticated, and that userInfo is loaded.
   * @param {string} identifier Airport identifier to remove.
   * @returns {Promise<void>}
   */
  async removeFavoriteAirport(identifier: string): Promise<void> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
    }
    if (!identifier) {
      throw new Error('Missing identifier');
    }

    // Check if favorite is already removed.
    const index = userInfo.favoriteAirports.findIndex(f => f.identifier === identifier);
    let existingFavorite: FavoriteAirport = null;
    if (index >= 0) {
      existingFavorite = userInfo.favoriteAirports[index];
    } else {
      return;
    }

    // Remove the favorite
    await this._http.delete(`${environment.airportsApiUrl}/customer/favorites/${existingFavorite.userDataId}`).toPromise();

    // Update userInfo
    userInfo.favoriteAirports.splice(index, 1);
    this.userInfo.next(userInfo);
  }

  /**
   * inserts specified entity via api
   * @param {Review} review object containing rating and review information to save to ratings for this entity.
   * @param {IEntity} entity object containing summary information about this entity (airport or business).
   * @return {Promise<Review>} Resulting review record from API.
   */
  async postReview(review: Review, entity: IEntity): Promise<Review> {
      const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to save a rating.');
    }
    try {
      let response: any;
      // perform the upsert
      const BODY = this._getReviewJSON(review);
      if (entity.type === 'Airport') {
        response = await this._http.post(`${environment.airportsApiUrl}/airports/${entity.id}/review`, BODY).toPromise();
      } else if (entity.type === 'Business') {
        response = await this._http.post(`${environment.airportsApiUrl}/businesses/${entity.id}/review`, BODY).toPromise();
      } else if (entity.type === 'Flying Club') {
        response = await this._http.post(`${environment.airportsApiUrl}/businesses/${entity.id}/review`, BODY).toPromise();
      } else {
        throw new Error('Invalid entity type');
      }
      return Review.parse(response);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * updates specified entity via api
   * @param {number} reviewId the id of the review to update.
   * @param {Review} review object containing rating and review information to save to ratings for this entity.
   * @param {IEntity} entity object containing summary information about this entity (airport or business).
   * @return {Promise<Review>} Resulting review record from API.
   */
  async putReview(reviewId: number, review: Review, entity: IEntity): Promise<Review> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to save a rating.');
    }
    try {
      let response: any;
      // perform the upsert
      const BODY = this._getReviewJSON(review);
      if (entity.type === 'Airport') {
        response = await this._http.put(`${environment.airportsApiUrl}/airports/${entity.id}/review/${reviewId}`, BODY).toPromise();
      } else if (entity.type === 'Business') {
        response = await this._http.put(`${environment.airportsApiUrl}/businesses/${entity.id}/review/${reviewId}`, BODY).toPromise();
      } else if (entity.type === 'Flying Club') {
        response = await this._http.put(`${environment.airportsApiUrl}/businesses/${entity.id}/review/${reviewId}`, BODY).toPromise();
      } else {
        throw new Error('Invalid entity type');
      }
      return Review.parse(response);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * converts entity to json obj
   * @param {Review} review
   * @return {any}
   * @private
   */
  _getReviewJSON (review: Review): any {
    return {
      title: review.title,
      review: review.review,
      rating: review.rating,
      subRatings: review.subRatings
    };
  }

  /**
   * updates specified entity via api
   * @param {number} reviewId the id of the review to update.
   * @param {number} photoId object containing a photo for this review.
   * @param {IEntity} entity object containing summary information about this entity (airport or business).
   * @return {Promise<Photo>} Resulting photo record from API.
   */
  async putReviewPhoto(reviewId: number, photoId: number, entity: IEntity): Promise<Photo> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add a photo to a review.');
    }
    try {
      let response: any;
      const BODY = this._getReviewPhotoJSON(reviewId, photoId, entity);
      if (entity.type === 'Airport') {
        response = await this._http.put(`${environment.airportsApiUrl}/photos/airport/update/${photoId}`, BODY).toPromise();
      } else if (entity.type === 'Business') {
        response = await this._http.put(`${environment.airportsApiUrl}/photos/business/update/${photoId}`, BODY).toPromise();
      } else {
        throw new Error('Invalid entity type');
      }
      return Photo.parse(response);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * converts entity to json obj
   * @param {number} reviewId
   * @param {number} photoId
   * @param {IEntity} entity
   * @return {any}
   * @private
   */
  _getReviewPhotoJSON (reviewId: number, photoId: number, entity: IEntity): any {
    return {
      photoId: photoId,
      entityId: entity.id,
      reviewId: reviewId
    };
  }

  /**
   * Gets a user review for an entity (airport or business). Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {IEntity} entity object containing summary information about this entity (airport or business).
   * @returns {Promise<Review>} User's review for entity from API.
   */
  async getUserReview(entity: IEntity): Promise<Review> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to save a rating.');
    }

    let response: any;
    if (entity.type === 'Airport') {
      response = await this._http.get(`${environment.airportsApiUrl}/airports/${entity.id}/reviews`).toPromise();
    } else if (entity.type === 'Business') {
      response = await this._http.get(`${environment.airportsApiUrl}/businesses/${entity.id}/reviews`).toPromise();
    } else if (entity.type === 'Flying Club') {
      response = await this._http.get(`${environment.airportsApiUrl}/businesses/${entity.id}/reviews`).toPromise();
    } else {
      throw new Error('Invalid entity type');
    }
    if (response.review) {
      return Review.parse(response.review);
    }

    return null;
  }

  /**
   * Gets the review summary for an entity (airport or business).
   * @param {IEntity} entity object containing summary information about this entity (airport or business).
   * @returns {Promise<ReviewSummary>} review summary for entity from API.
   */
  async getReviewSummary(entity: IEntity): Promise<ReviewSummary> {
    let response: any;
    if (entity.type === 'Airport') {
      response = await this._http.get(`${environment.airportsApiUrl}/airports/${entity.id}/reviews`).toPromise();
    } else if (entity.type === 'Business') {
      response = await this._http.get(`${environment.airportsApiUrl}/businesses/${entity.id}/reviews`).toPromise();
    } else if (entity.type === 'Flying Club') {
      response = await this._http.get(`${environment.airportsApiUrl}/businesses/${entity.id}/reviews`).toPromise();
    } else {
      throw new Error('Invalid entity type');
    }
    return ReviewSummary.parse(response.reviewSummary);
  }

  /**
   * Sets the given airport identifier to the user's home airport. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {string} identifier Home identifier to set.
   * @returns {Promise<string>} Home airport record.
   */
  async setHomeAirport(identifier: string): Promise<string> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
    }
    if (!identifier) {
      throw new Error('Missing identifier');
    }

    // Check if home already exists
    if (identifier === userInfo.homeAirportIdent) {
      return identifier;
    }

   // Add as the home
    const response = await this._http.post(`${environment.airportsApiUrl}/customer/homeairport/${identifier}`, null).toPromise();

    // Update userInfo
    userInfo.homeAirportIdent = identifier;
    this.userInfo.next(userInfo);

    return identifier;
  }

  /**
   * Sets an interaction for this comment (thumb up or down, offensive. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {number} airportId airport.
   * @param {number} reviewId review id.
   * @param {string} interactionType The interaction to save {Offensive,ClearOffensive,ClearVote,UpVote,DownVote}.
   * @returns {Promise<void>} Nothing.
   */
  async setAirportInteraction(airportId: number, reviewId: number, interactionType: string): Promise<any> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
    }
    if (!airportId || !reviewId || !interactionType) {
      throw new Error('Missing information');
    }

    const newInteraction = {
      Interaction: interactionType
    };
    const response = await this._http.post(`${environment.airportsApiUrl}/airports/${airportId}/review/${reviewId}/interaction`,
      newInteraction).toPromise();

    return response;
  }

  /**
   * Sets an interaction for this comment (thumb up or down, offensive. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {number} businessId business.
   * @param {number} reviewId review id.
   * @param {string} interactionType The interaction to save {Offensive,ClearOffensive,ClearVote,UpVote,DownVote}.
   * @returns {Promise<void>} Nothing.
   */
  async setBusinessInteraction(businessId: number, reviewId: number, interactionType: string): Promise<any> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
    }
    if (!businessId || !reviewId || !interactionType) {
      throw new Error('Missing information');
    }

    const newInteraction = {
      Interaction: interactionType
    };
    const response = await this._http.post(`${environment.airportsApiUrl}/businesses/${businessId}/review/${reviewId}/interaction`,
      newInteraction).toPromise();

    return response;
  }

  /**
   * Sets an interaction for this aviation event
   * @param {number} aviationEventId aviation event
   * @param {string} interactionType The interaction to save {Offensive,ClearOffensive,ClearVote}.
   * @returns {Promise<void>} Nothing.
   */
  async setAviationEventInteraction(aviationEventId: number, interactionType: string): Promise<any> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an interaction.');
    }
    if (!aviationEventId || !interactionType) {
      throw new Error('Missing information');
    }

    const newInteraction = {
      Interaction: interactionType
    };
    const response = await this._http.post(`${environment.airportsApiUrl}/events/${aviationEventId}/interaction`,
      newInteraction).toPromise();

    return response;
  }

  /**
   * Sets an interaction for this comment (thumb up or down, offensive. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {number} airportId airport.
   * @param {number} reviewId review id.
   * @returns {Promise<void>} Nothing.
   */
  async deleteAirportReview(airportId: number, reviewId: number): Promise<any> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
    }
    if (!airportId || !reviewId ) {
      throw new Error('Missing information');
    }

    const response = await this._http.delete(`${environment.airportsApiUrl}/airports/${airportId}/review/${reviewId}`).toPromise();

    return response;
  }

  /**
   * Sets an interaction for this comment (thumb up or down, offensive. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {number} businessId business.
   * @param {number} reviewId review id.
   * @returns {Promise<void>} Nothing.
   */
  async deleteBusinessReview(businessId: number, reviewId: number): Promise<any> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
    }
    if (!businessId || !reviewId ) {
      throw new Error('Missing information');
    }

    const response = await this._http.delete(`${environment.airportsApiUrl}/businesses/${businessId}/review/${reviewId}`).toPromise();

    return response;
  }

  /**
   * Moves a review. Requires that the user is authenticated, and that userInfo is loaded.
   * @param {string} fromType either airport or business
   * @param {number} fromId the entities Id the review is being removed from
   * @param {string} toType either airport or business
   * @param {number} toId the entities Id the review is being removed to
   * @param {number} reviewId the Id of the review
   * @returns {Promise<void>} Nothing.
   */
  async moveReview(fromType: string, fromId: number, toType: string, toId: number, reviewId: number): Promise<any> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
    }
    if (!fromType || !fromId || !toType || !toId || !reviewId ) {
      throw new Error('Missing information');
    }
    // const BODY = this._getReviewPhotoJSON(reviewId, photoId, entity);
    const BODY = {
      reviewId: reviewId,
      fromType: fromType,
      fromId: fromId,
      toType: toType,
      toId: toId
    };
    const response = await this._http.put(`${environment.airportsApiUrl}/reviews/move`, BODY).toPromise();

    return response;
  }

  /**
   * Deletes a photo associated with a review
   * @param {number} photoId the id for an airport photo
   * @param {string} entityType type of entity to remove the photo from
   * @returns {Promise<void>} Nothing.
   */
  async deleteReviewPhoto(photoId: number, entityType: string): Promise<any> {
    const userInfo = this.userInfo.getValue();
    let entityAPI = 'business';
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to remove a review photo.');
    }
    if (!photoId || !entityType ) {
      throw new Error('Missing information');
    }
    if (entityType === 'Airport') {
      entityAPI = 'airport';
    }

    const response = await this._http.delete(`${environment.airportsApiUrl}/photos/${entityAPI}/delete/${photoId}`).toPromise();

    return response;
  }

  /**
   * Removes the given airport identifier as the user's home airport. Requires that the user is
   * authenticated, and that userInfo is loaded.
   * @param {string} identifier Airport identifier to remove.
   * @returns {Promise<void>}
   */
  async removeHomeAirport(): Promise<void> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add an airport favorite.');
    }

    // Remove the favorite
    await this._http.post(`${environment.airportsApiUrl}/customer/homeairport/`, null).toPromise();

    // Update userInfo
    userInfo.homeAirportIdent = '';
    this.userInfo.next(userInfo);
  }

  /**
   * Sets the given airport identifier to the user's home airport. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {string} identifier Home identifier to set.
   * @returns {Promise<string>} Home airport record.
   */
  async setAircraftType(aircraftType: string): Promise<string> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to set a default aircraft type.');
    }
    if (!aircraftType) {
      throw new Error('Missing aircraft type.');
    }

    // Check if home already exists
    if (aircraftType === userInfo.aircraftType) {
      return aircraftType;
    }

    // Add as the home
    const response = await this._http.post(`${environment.airportsApiUrl}/customer/aircraftType/${aircraftType}`, null).toPromise();

    // Update userInfo
    userInfo.aircraftType = aircraftType;
    this.userInfo.next(userInfo);

    return aircraftType;
  }

  /**
   * Gets the authenticated user's UserEcho token. Note this method requires that the user is authenticated.
   * @returns {Promise<string>} SSO token
   */
  async getUserechoInfo(): Promise<string> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      return null;
    }
    try {
      const response = await this._http.get(`https://webservice.aopa.org/userecho/userinfo.cfm`, { withCredentials: true }).toPromise();
      const userEcho = UserEcho.parse(response);
      return userEcho.userEchoToken;
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  async getWhatsNewVersion(): Promise<string> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      return null;
    }
    const URL = `${environment.airportsApiUrl}/customer/userData`;
    try {
      const response: any = await this._http.get(URL).toPromise();
      const whatsNewObject = response.find((obj) => {
        return obj.dataPointId === 3007;
      });
      if (whatsNewObject) {
        return whatsNewObject.dataValue;
      }
      return null;
    } catch (e) {
      this._errorHandler.handleError(e);
      return null;
    }
  }

  async saveWhatsNewVersion() {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      return;
    }
    const data = {
      dataName: 'WhatsNewVersion',
      dataValue: environment.version
    };
    const URL = `${environment.airportsApiUrl}/customer/userData`;
    try {
      const RESPONSE = await this._http.post(URL, data).toPromise();
      return;
    } catch (e) {
      this._errorHandler.handleError(e);
      return;
    }
  }

  /**
   * Adds the given event id to the user's list of favorite events. Requires that the user is authenticated,
   * and that userInfo is loaded.
   * @param {number} eventId event id to add.
   * @returns {Promise<FavoriteAirport>} Favorite event record.
   */
  async addFavoriteEvent(eventId: number): Promise<FavoriteEvent> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available in order to add a favorite event.');
    }
    if (!eventId) {
      throw new Error('Missing event id');
    }

    // Check if favorite already exists
    const existingFavorite = userInfo.favoriteEvents.find(f => f.eventId === eventId);
    if (existingFavorite) {
      return existingFavorite;
    }

    // Add the favorite
    const response = await this._http.post(`${environment.airportsApiUrl}/customer/favorites/event/${eventId}`, null).toPromise();
    const favoriteEvent = FavoriteEvent.parse(response);

    // Update userInfo
    userInfo.favoriteEvents.push(favoriteEvent);
    this.userInfo.next(userInfo);

    return favoriteEvent;
  }

  /**
   * Removes the given event id from the user's list of favorite events. Requires that the user is
   * authenticated, and that userInfo is loaded.
   * @param {number} eventId event id to remove.
   * @returns {Promise<void>}
   */
  async removeFavoriteEvent(eventId: number): Promise<void> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      throw new Error('User must be authenticated and user info must be available to remove a favorite event.');
    }
    if (!eventId) {
      throw new Error('Missing identifier');
    }

    // Check if favorite is already removed.
    const index = userInfo.favoriteEvents.findIndex(f => f.eventId === eventId);
    let existingFavorite: FavoriteEvent = null;
    if (index >= 0) {
      existingFavorite = userInfo.favoriteEvents[index];
    } else {
      return;
    }

    // Remove the favorite
    await this._http.delete(`${environment.airportsApiUrl}/customer/favorites/${existingFavorite.userDataId}`).toPromise();

    // Update userInfo
    userInfo.favoriteEvents.splice(index, 1);
    this.userInfo.next(userInfo);
  }

  async getShowAirportsFlag(): Promise<boolean> {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      return null;
    }
    const URL = `${environment.airportsApiUrl}/customer/userData`;
    try {
      const response: any = await this._http.get(URL).toPromise();
      const showAirportsObject = response.find((obj) => {
        return obj.dataPointId === 6001;
      });
      if (showAirportsObject) {
        if (showAirportsObject.dataValue === 'true' || showAirportsObject.dataValue === '1') {
          return true;
        } else {
          return false;
        }
      }
      return null;
    } catch (e) {
      this._errorHandler.handleError(e);
      return null;
    }
  }

  async saveShowAirportsFlag(newValue: boolean) {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      return;
    }
    let stringValue;
    if (newValue) {
      stringValue = '1';
    } else {
      stringValue = '0';
    }
    const data = {
      dataName: 'ShowAirportsFlag',
      dataValue: stringValue
    };
    const URL = `${environment.airportsApiUrl}/customer/userData`;
    try {
      const RESPONSE = await this._http.post(URL, data).toPromise();
      return;
    } catch (e) {
      this._errorHandler.handleError(e);
      return;
    }
  }

  async saveAcceptedTermsAndPrivacy() {
    const userInfo = this.userInfo.getValue();
    if (!userInfo) {
      return;
    }
    const data = {
      dataName: 'AcceptedTerms',
      dataValue: '1'
    };
    const URL = `${environment.airportsApiUrl}/customer/userData`;
    try {
      const RESPONSE = await this._http.post(URL, data).toPromise();
      return;
    } catch (e) {
      this._errorHandler.handleError(e);
      return;
    }
  }
}
